import React, { useEffect, useState, useMemo } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import LockIcon from '@mui/icons-material/Lock';

import M from 'messages';
import useDebounce from 'components/common/customDebounceHook/useDebounce';
import domainData from 'store/domainStore';
import routes from 'config/route';
import classes from './styles';

const DEFAULT_SELECT_MAX_WIDTH = 250;

const CustomFilterSelect = ({ field, onFilterCallback, searchValue = '', isAvailable }) => {
  const memoizedFilterValue = useMemo(() => ({ [field.id]: searchValue }), [field.id, searchValue]);
  const [searchTerm, setSearchTerm] = React.useState(memoizedFilterValue);
  const debouncedValue = useDebounce(typeof searchTerm === 'object' ? searchTerm : { [field?.id]: searchTerm }, 1000);
  const [modifiedOptions, setModifiedOptions] = useState([]);

  useEffect(() => {
    setSearchTerm({ [field.id]: searchValue });
  }, [field, searchValue]);

  useEffect(() => {
    if (onFilterCallback) {
      onFilterCallback(debouncedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const getCurrentServiceProvider = () => {
    const currentSP = domainData.getCurrentSP();
    const currSP = currentSP ? { currentServiceProvider: currentSP } : {};
    return currSP;
  };

  const validateFilterValues = availableOptions => {
    const currentFilterValue = searchTerm?.[field.id];
    if (!currentFilterValue === null || currentFilterValue === undefined || currentFilterValue === '') return;

    const availableValues = availableOptions.map(option => (option.id || option.value).toString());
    const isValid = availableValues.includes(currentFilterValue?.toString());

    // If validation fails, reset the filter
    if (!isValid) {
      onFilterCallback({}, true);
    }
  };

  useEffect(() => {
    if (typeof field.options === 'function') {
      const newParams = { params: { ...getCurrentServiceProvider() } };
      field.options(newParams).then(res => {
        const newOptions = field.adapterCallback(res.data.data);
        setModifiedOptions(newOptions);
        validateFilterValues(newOptions);
      });
    } else {
      setModifiedOptions(field.options);
      validateFilterValues(field.options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field]);

  const handleOnChang = e => {
    if (!isAvailable) return; // Don't allow changes if not available
    const { value } = e.target;
    setSearchTerm({ [field.id]: value });
  };

  const renderLockIcon = () => (
    <Tooltip
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': classes.tooltip,
        },
      }}
      title={
        <Typography sx={classes.noAccess}>
          {M.get('subscriptions.noAccess1')}
          &nbsp;
          <a style={classes.link} href={`${routes.subscriptions.path}?type=standard`} rel="noopener noreferrer">
            {M.get('subscriptions.upgrade')}
          </a>
          &nbsp;
          {M.get('subscriptions.noAccess2')}
        </Typography>
      }
      placement="top">
      <LockIcon />
    </Tooltip>
  );

  const getEndAdornment = () => {
    if (!isAvailable) {
      return <Box sx={classes.lockIcon}>{renderLockIcon()}</Box>;
    }
    return null;
  };

  return (
    <Box
      sx={classes.box}
      style={field.maxWidth ? { maxWidth: field.maxWidth } : { maxWidth: DEFAULT_SELECT_MAX_WIDTH }}>
      <FormControl margin="dense" fullWidth variant="outlined">
        <InputLabel size="small" sx={classes.label} shrink={false}>
          {searchTerm?.[field.id] ? '' : M.get(`${field.label}`)}
        </InputLabel>
        <Select
          variant="outlined"
          endAdornment={getEndAdornment()}
          disabled={!isAvailable}
          sx={{
            '& .MuiBox-root': classes.selectElement,
            ...(searchTerm?.[field.id] && classes[field.selectedClassName]),
            ...classes.selectRoot,
            ...(!isAvailable && classes.disabledSelect),
          }}
          MenuProps={{
            classes: { paper: classes.menuPaper },
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          }}
          value={searchTerm?.[field.id] ?? ''}
          onChange={handleOnChang}
          name={field.id}>
          <MenuItem value="" key="empty">
            {' '}
            <em>
              <Box sx={classes.menuItem}>{field.insteadNone || M.get('filterFields.none')}</Box>
            </em>{' '}
          </MenuItem>
          {modifiedOptions?.map(opt => (
            <MenuItem key={opt.id || opt.value} value={opt.id || opt.value}>
              <Box sx={classes.menuItem} style={field.maxWidth ? { maxWidth: field.maxWidth } : {}}>
                {opt.title}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

CustomFilterSelect.propTypes = {
  onFilterCallback: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(Object).isRequired,
  searchValue: PropTypes.oneOfType([PropTypes.instanceOf(Array), PropTypes.string]),
  isAvailable: PropTypes.bool.isRequired,
};

export default CustomFilterSelect;
